import React from "react";
import { Box, Chip, Divider, Grid2, Paper, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  DailyTasksDetailResult,
  DailyTasksSlaStatus,
  PRINT_WORK_STATE_LABELS,
  PrintWorkState,
  PurchaseOrderDrawerDto,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { dailyTasksChipSx, getDailyTasksChipColors } from "./dailyTasksChips";
import { DailyTasksHint, DAILY_TASKS_HINTS } from "./DailyTasksHint";
import { IdLink, purchaseRequestDetailsPath } from "./DailyTasksMedia";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import { DailyTasksEmptyState } from "./DailyTasksEmptyState";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";

interface PrintDrawerBodyProps {
  detail: DailyTasksDetailResult;
}

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const printWorkLabel = (
  state: DailyTasksDetailResult["mockup_work_state"],
): string => {
  if (state && state in PRINT_WORK_STATE_LABELS) {
    return PRINT_WORK_STATE_LABELS[state as PrintWorkState];
  }
  return state ? state.replace(/_/g, " ") : "Fully sent";
};

const slaTone = (
  theme: Theme,
  status: DailyTasksSlaStatus,
): { bg: string; fg: string; label: string } => {
  try {
    if (status === "bad") {
      return { ...getDailyTasksChipColors(theme, "bad"), label: "Overdue" };
    }
    if (status === "warn") {
      return { ...getDailyTasksChipColors(theme, "warn"), label: "Watch" };
    }
    if (status === "ok") {
      return { ...getDailyTasksChipColors(theme, "ok"), label: "On track" };
    }
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  } catch (error) {
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  }
};

const StatTile: React.FC<{
  eyebrow: string;
  value: string;
  hint?: string;
  info?: string;
  pill?: { label: string; bg: string; fg: string };
}> = ({ eyebrow, value, hint, info, pill }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          sx={{
            color: colors.greyTextColor,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </CustomTypography>
        {info ? <DailyTasksHint text={info} /> : null}
      </Box>
      <CustomTypography
        variant="heading5"
        fontWeight={600}
        sx={{
          color: colors.navyText,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </CustomTypography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 0.75,
        }}
      >
        {pill ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: pill.bg,
              color: pill.fg,
            }}
          >
            <CustomTypography variant="caption" fontWeight={700} color={pill.fg}>
              {pill.label}
            </CustomTypography>
          </Box>
        ) : null}
        {hint ? (
          <CustomTypography variant="caption" sx={{ color: colors.greyTextColor }}>
            {hint}
          </CustomTypography>
        ) : null}
      </Box>
    </Box>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <CustomTypography
      variant="caption"
      fontWeight={600}
      sx={{
        color: getModernPageColors(theme).subtitle,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        display: "block",
        mb: 1.25,
      }}
    >
      {children}
    </CustomTypography>
  );
};

const MetaField: React.FC<{
  label: string;
  value: string;
  hint?: string;
}> = ({ label, value, hint }) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minHeight: 20 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          sx={{
            color: pageColors.subtitle,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </CustomTypography>
        {hint ? <DailyTasksHint text={hint} /> : null}
      </Box>
      <CustomTypography
        variant="body2"
        fontWeight={500}
        sx={{ color: pageColors.title, mt: 0.25 }}
      >
        {value}
      </CustomTypography>
    </Grid2>
  );
};

const workStateTone = (
  state: DailyTasksDetailResult["mockup_work_state"],
): "bad" | "warn" | "info" | "ok" => {
  if (state === "needs_outward") return "bad";
  if (state === "partial_outward") return "info";
  return "ok";
};

const clockHintFor = (
  state: DailyTasksDetailResult["mockup_work_state"],
): string => {
  if (state === "partial_outward") return "Since the first real outward";
  return "Since raw QC cleared";
};

const PoLine: React.FC<{
  order: PurchaseOrderDrawerDto;
  orderId: number;
}> = ({ order, orderId }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const prHref =
    order.pr_id != null
      ? purchaseRequestDetailsPath(order.pr_id, orderId)
      : null;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: colors.veryLightGray,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {prHref ? (
          <IdLink
            href={prHref}
            label={`PO ${order.po_number || order.po_id}`}
            fontSize="0.8rem"
          />
        ) : (
          <CustomTypography variant="body3" fontWeight={600}>
            PO {order.po_number || order.po_id}
          </CustomTypography>
        )}
      </Box>
      <CustomTypography
        variant="caption"
        sx={{ color: colors.greyTextColor, display: "block", mt: 0.5 }}
      >
        {order.vendor_name ? `${order.vendor_name} · ` : ""}
        {order.totals.sent}/{order.totals.ordered} sent
        {order.printer_name ? ` · ${order.printer_name}` : ""}
        {order.timeline ? ` · Timeline ${formatDate(order.timeline)}` : ""}
      </CustomTypography>
    </Box>
  );
};

const PrintDrawerBody: React.FC<PrintDrawerBodyProps> = ({ detail }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const sla = slaTone(theme, detail.sla_status);
  const daysHint =
    detail.days_to_sla == null
      ? null
      : detail.days_to_sla < 0
        ? `${Math.abs(detail.days_to_sla)} day${
            Math.abs(detail.days_to_sla) === 1 ? "" : "s"
          } past SLA`
        : detail.days_to_sla === 0
          ? "Due today"
          : `${detail.days_to_sla} day${detail.days_to_sla === 1 ? "" : "s"} left`;
  const clockHint = clockHintFor(detail.mockup_work_state);
  const orders = detail.purchase_orders ?? [];
  const sent = Math.max(
    detail.sent_quantity ?? 0,
    ...orders.map((order) => order.totals.sent ?? 0),
    0,
  );
  const ordered = detail.ordered_quantity ?? 0;
  const qtyPct =
    ordered > 0 ? Math.min(100, Math.round((sent / ordered) * 100)) : 0;
  const remaining = Math.max(ordered - sent, 0);
  const slaDays =
    detail.print_outward_sla_days == null
      ? "No SLA"
      : `${detail.print_outward_sla_days}d print`;

  return (
    <>
      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Elapsed"
              value={formatHours(detail.sla_elapsed_hours)}
              hint={`Since ${formatDate(detail.sla_clock_start)}`}
              info={DAILY_TASKS_HINTS.drawerPrintElapsed}
              pill={{ label: sla.label, bg: sla.bg, fg: sla.fg }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Queue SLA"
              value={formatHours(detail.sla_target_hours)}
              hint={daysHint ?? `${slaDays} · ${clockHint}`}
              info={DAILY_TASKS_HINTS.drawerPrintSla}
            />
          </Grid2>
        </Grid2>
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={printWorkLabel(detail.mockup_work_state)}
            sx={dailyTasksChipSx(theme, workStateTone(detail.mockup_work_state))}
          />
          <CustomTypography
            variant="caption"
            sx={{ color: colors.greyTextColor, alignSelf: "center" }}
          >
            {clockHint}
          </CustomTypography>
        </Box>
        <Box sx={{ mt: 1.75 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.greyTextColor }}>
                Outward
              </CustomTypography>
              <DailyTasksHint text={DAILY_TASKS_HINTS.drawerPrintSent} />
            </Box>
            <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.navyText }}>
              {sent} / {ordered}
              {remaining > 0 ? ` · ${remaining} still to outward` : ""}
            </CustomTypography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: colors.veryLightGray,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${qtyPct}%`,
                height: "100%",
                bgcolor: remaining === 0 ? colors.successGreen : colors.darkBlue,
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), mt: 2 }}>
        <SectionLabel>Order</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField label="Type" value={detail.order_type || "-"} />
          <MetaField label="Qty" value={String(detail.ordered_quantity ?? 0)} />
          <MetaField label="PM" value={detail.pm_poc || "Unassigned"} />
          <MetaField label="Sales" value={detail.sales_poc || "-"} />
          {detail.printer_name ? (
            <MetaField label="Printer" value={detail.printer_name} />
          ) : null}
          {canViewDispatchDate && detail.dispatch_date ? (
            <MetaField label="Dispatch" value={formatDate(detail.dispatch_date)} />
          ) : null}
          <MetaField
            label="Total TAT"
            value={formatHours(detail.total_tat_hours)}
            hint={DAILY_TASKS_HINTS.drawerTotalTat}
          />
        </Grid2>
        <Divider sx={{ my: 2 }} />
        <SectionLabel>Purchase orders</SectionLabel>
        {orders.length === 0 ? (
          <DailyTasksEmptyState
            inPaper={false}
            icon={LocalMallOutlinedIcon}
            title="No purchase orders yet"
            description="Issued POs for this product will show here with print outward details."
          />
        ) : (
          orders.map((order) => (
            <PoLine key={order.po_id} order={order} orderId={detail.order_id} />
          ))
        )}
      </Paper>
    </>
  );
};

export default PrintDrawerBody;
